import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GalleryPage = () => {
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const params = filter !== "all" ? { category: filter } : {};
        const response = await axios.get(`${API}/projects`, { params });
        setProjects(response.data);
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [filter]);

  const filters = [
    { value: "all", label: "All Projects" },
    { value: "bathroom", label: "Bathrooms" },
    { value: "kitchen", label: "Kitchens" },
    { value: "floor", label: "Floors" },
    { value: "feature_wall", label: "Feature Walls" }
  ];

  // Real DC Tiling project images - each as unique card
  const sampleImages = [
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/42w9spxp_Hexagon%20tiling%20bathroom.JPG", category: "bathroom", title: "Hexagon Bathroom Tiling" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/dnlvpa0u_Bathroom.JPG", category: "bathroom", title: "Modern Bathroom Installation" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/82pp4ivu_Bathroom2%20.JPG", category: "bathroom", title: "Contemporary Bathroom Design" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/cpzzcdg3_Bathroom.JPG", category: "bathroom", title: "Classic Bathroom Tiling" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/guu9j793_Bathroom2%20.JPG", category: "bathroom", title: "Stylish Bathroom Renovation" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/ce0b56xr_Niche%20feature%20tiling.JPG", category: "bathroom", title: "Niche Feature Tiling" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/r00vkwt3_Cloakroom3.JPG", category: "bathroom", title: "Luxury Cloakroom" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/39lhfgld_Cloakroom%202.JPG", category: "bathroom", title: "Modern Cloakroom Design" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/9oqt5hl3_Cloakroom%202.JPG", category: "bathroom", title: "Compact Cloakroom Tiling" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/or3lfvx3_Cloakroom3.JPG", category: "bathroom", title: "Premium Cloakroom Finish" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/7b3cl7yx_Splashback%20tiling.JPG", category: "kitchen", title: "Kitchen Splashback" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/w8bfwhom_Fire%20place%20tiling.JPG", category: "feature_wall", title: "Fireplace Feature Tiling" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/hvgewxd3_Victorian%20floor%20tiles.JPG", category: "floor", title: "Victorian Floor Pattern" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/2zgvcgl7_Victorian%20floor%20tiles%202.JPG", category: "floor", title: "Victorian Hallway Tiles" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/rlyl4bqt_Victorian%20floor%20tiles%203.JPG", category: "floor", title: "Traditional Victorian Floor" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/e5ceugk5_Wood%20effect%20porcelain%20floor%20tiles.JPG", category: "floor", title: "Wood Effect Porcelain" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/ruy140nk_Wood%20effect%20porcelain%20floor%20tiling%202.JPG", category: "floor", title: "Natural Wood Effect Tiles" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/i16zedmm_Oak%20effect%20porcelain%20floor%20tiles%202.JPG", category: "floor", title: "Oak Effect Porcelain Floor" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/a56k6rjv_Floor%20tiling.JPG", category: "floor", title: "Modern Floor Installation" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/gkil5qdr_Floor%20tiling%20.JPG", category: "floor", title: "Large Format Floor Tiles" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/qlt1yalp_Floor%20tiling%20.JPG", category: "floor", title: "Premium Floor Tiling" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/qnhkb268_Herringbone%20tiling.JPG", category: "floor", title: "Herringbone Pattern Floor" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/mm3sw4gg_Feature%20Victorian%20tiling.JPG", category: "feature_wall", title: "Victorian Feature Tiles" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/nnvp8008_Feature%20Victorian%20tiling%202.JPG", category: "feature_wall", title: "Decorative Victorian Wall" },
    { url: "https://customer-assets.emergentagent.com/job_tiling-calculator/artifacts/o9wxhr7r_Feature%20Victorian%20tiling%202.JPG", category: "feature_wall", title: "Victorian Accent Wall" }
  ];

  const galleryImages = projects.length > 0 
    ? projects.flatMap(p => p.images.map(img => ({ url: img, category: p.category })))
    : sampleImages;

  const filteredImages = filter === "all" 
    ? galleryImages 
    : galleryImages.filter(img => img.category === filter);

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section className="py-20 bg-gray-50 grid-pattern">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 font-montserrat">
            Project Gallery
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Browse our portfolio of completed tiling projects across Greater Manchester.
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b border-gray-100 sticky top-20 z-30">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-2">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-6 py-2 rounded-full font-medium transition-all ${
                  filter === f.value
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="aspect-square skeleton rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="gallery-masonry">
              {filteredImages.map((image, index) => (
                <div 
                  key={index}
                  className="gallery-item cursor-pointer"
                  onClick={() => setLightboxImage(image.url)}
                  data-testid={`gallery-item-${index}`}
                >
                  <img
                    src={image.url}
                    alt={`Tiling project ${index + 1}`}
                    className="w-full rounded-xl hover:opacity-90 transition-opacity"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div className="text-center mt-12">
            <Link to="/quote">
              <Button size="lg" className="bg-primary hover:bg-primary/90" data-testid="gallery-cta">
                Get a Quote For Similar Project
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="lightbox-overlay"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox"
        >
          <button 
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
            onClick={() => setLightboxImage(null)}
            data-testid="lightbox-close"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={lightboxImage}
            alt="Gallery lightbox"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
